import type {
    DefaultError,
    InfiniteData,
    QueryFunctionContext,
    QueryKeyWithDataTag,
    UnusedSkipTokenInfiniteOptions
} from "@tanstack/react-query";
import type { Client, FetchResponse, MaybeOptionalInit } from "openapi-fetch";
import type { HttpMethod, MediaType, PathsWithMethod } from "openapi-typescript-helpers";

type InfiniteQueryKey<TMethod extends HttpMethod, TPath, TInit> = TInit extends undefined
    ? readonly [TMethod, TPath]
    : readonly [TMethod, TPath, TInit];

type PageQuery<TOperation> = TOperation extends { parameters: { query?: infer TQuery } }
    ? Partial<NonNullable<TQuery>>
    : never;

type InfiniteQueryOptionsFunction<TPaths extends Record<string, Record<HttpMethod, {}>>, TMedia extends MediaType> = <
    TMethod extends HttpMethod,
    TPath extends PathsWithMethod<TPaths, TMethod>,
    TInit extends MaybeOptionalInit<TPaths[TPath], TMethod>,
    TResponse extends Required<FetchResponse<TPaths[TPath][TMethod], TInit, TMedia>>,
    TPageParam,
    TData = InfiniteData<TResponse["data"], TPageParam>
>(
    method: TMethod,
    path: TPath,
    init: TInit,
    options: Omit<
        UnusedSkipTokenInfiniteOptions<
            TResponse["data"],
            DefaultError,
            TData,
            InfiniteQueryKey<TMethod, TPath, TInit>,
            TPageParam
        >,
        "queryKey" | "queryFn"
    > & {
        toPageQuery: (pageParam: TPageParam) => PageQuery<TPaths[TPath][TMethod]>;
    }
) => NoInfer<
    UnusedSkipTokenInfiniteOptions<
        TResponse["data"],
        DefaultError,
        TData,
        InfiniteQueryKey<TMethod, TPath, TInit>,
        TPageParam
    > &
        QueryKeyWithDataTag<InfiniteQueryKey<TMethod, TPath, TInit>, InfiniteData<TResponse["data"], TPageParam>>
>;

type ClientMethod = (path: PropertyKey, init: unknown) => Promise<{ data: unknown; error: unknown }>;

interface KeyInit {
    params?: { query?: Record<string, unknown> };
}

interface ImplementationOptions {
    toPageQuery: (pageParam: unknown) => object;
}

export interface InfiniteQueryDefaults {
    staleTime?: number;
    gcTime?: number;
}

interface PageNumberPage {
    pagination: { page: number; pageSize: number; totalFiltered: number };
}

export const getPageNumberNextPageParam = (lastPage: PageNumberPage): number | undefined => {
    const { page, pageSize, totalFiltered } = lastPage.pagination;

    return page * pageSize < totalFiltered ? page + 1 : undefined;
};

export const pageNumberPagination = {
    toPageQuery: (pageParam: number) => ({ pageNumber: pageParam }),
    initialPageParam: 1,
    getNextPageParam: getPageNumberNextPageParam
};

/**
 * Infinite counterpart of openapi-react-query's `queryOptions`: the key is `queryOptions(method, path, init).queryKey`,
 * and the `queryFn` sends the init read back from that key, with `toPageQuery(pageParam)` merged into its query.
 */
export const createInfiniteQueryOptions = <TPaths extends {}, TMedia extends MediaType = MediaType>(
    client: Client<TPaths, TMedia>,
    defaults?: InfiniteQueryDefaults
): InfiniteQueryOptionsFunction<TPaths, TMedia> =>
    ((
        method: HttpMethod,
        path: string,
        init: KeyInit | undefined,
        { toPageQuery, ...options }: ImplementationOptions
    ) => ({
        ...defaults,
        ...options,
        queryKey: init === undefined ? [method, path] : [method, path, init],
        queryFn: async ({
            queryKey: [keyMethod, keyPath, keyInit],
            pageParam,
            signal
        }: QueryFunctionContext<readonly [HttpMethod, string, KeyInit?], unknown>) => {
            const requestMethod = keyMethod.toUpperCase();
            const { params, ...restInit } = keyInit ?? {};
            const fn = (client as unknown as Record<string, ClientMethod>)[requestMethod];
            const { data, error } = await fn(keyPath, {
                ...restInit,
                signal,
                params: {
                    ...params,
                    query: { ...params?.query, ...toPageQuery(pageParam) }
                }
            });
            if (error) {
                throw error;
            }
            if (data === undefined) {
                throw new Error(`${requestMethod} ${keyPath} returned no body.`);
            }

            return data;
        }
    })) as unknown as InfiniteQueryOptionsFunction<TPaths, TMedia>;
